/* eslint-disable metabase/no-color-literals */
import type { MetabaseThemeV2 } from "../../types";
import { DARK_THEME_ACCENT_COLORS } from "../accent-colors";
import { getBaseColorsForThemeDefinitionOnly } from "../base-colors";
import { SEMIPUBLIC_COLORS as sp } from "../semipublic";

const baseColors = getBaseColorsForThemeDefinitionOnly();

// Semipublic forest only reaches ~1.6:1 on dark grounds, so brand and other
// interactive tokens step up to the lighter green/sky/coral ramp stops here.

// To be removed once the rename in GDGT-2517 is complete. Prefer the keys in
// `actualColors` below.
const deprecatedColors = {
  "admin-navbar": sp["forest-800"],
  "admin-navbar-secondary": baseColors.brand[30],
  "admin-navbar-inverse": baseColors.brand[60],
  "background-brand": baseColors.brand[90],
  "background-filter": baseColors.filter[90],
  "background-disabled": baseColors.orionAlphaInverse[10],
  "background-disabled-inverse": baseColors.orionAlpha[10],
  "background-error-secondary": baseColors.lobster[90],
  "background-highlight":
    "color-mix(in srgb, var(--mb-color-core-brand) 7%, transparent)",
  "background-hover":
    "color-mix(in srgb, var(--mb-color-core-brand) 7%, transparent)",
  "background-selected":
    "color-mix(in srgb, var(--mb-color-core-brand) 21%, transparent)",
  "background-primary": baseColors.orion[100],
  "background-secondary": baseColors.orion[110],
  "background-tertiary": baseColors.orion[80],
  "background-primary-inverse": baseColors.orion[20],
  "background-secondary-inverse": baseColors.orion[30],
  "background-tertiary-inverse": baseColors.orion[70],
  overlay: baseColors.orionAlpha[70],
  "background-error": baseColors.lobster[90],
  "background-success": baseColors.palm[90],
  "background-success-secondary": baseColors.palm[70],
  brand: sp["green-400"],
  "brand-hover": baseColors.brand[30],
  danger: sp["coral-400"],
  error: sp["coral-400"],
  filter: sp["sky-500"],
  focus: sp["green-400"],
  "icon-brand": "var(--mb-color-text-brand)",
  "icon-primary": "var(--mb-color-text-primary)",
  "icon-disabled": "var(--mb-color-text-disabled)",
  "icon-secondary": "var(--mb-color-text-secondary)",
  "illustration-brand-secondary": baseColors.brand[80],
  "illustration-brand-tertiary": baseColors.brand[90],
  "metabase-brand": baseColors.brand[40],
  "metabase-brand-hover": baseColors.brand[50],
  "saturated-blue": baseColors.ocean[40],
  "saturated-green": baseColors.palm[40],
  "saturated-purple": baseColors.octopus[40],
  "saturated-red": baseColors.lobster[40],
  "saturated-yellow": baseColors.dubloon[30],
  shadow: `color-mix(in srgb, ${baseColors.orion[110]} 20%, transparent)`,
  "success-secondary": sp["green-300"],
  success: sp["green-400"],
  summarize: sp["green-400"],
  "syntax-parameters-active": baseColors.mango[90],
  "syntax-parameters": baseColors.mango[40],
  "text-tertiary": baseColors.orionAlphaInverse[40],
  "text-tertiary-inverse": baseColors.orionAlpha[40],
  "text-hover": baseColors.brand[30],
  "text-secondary-opaque": baseColors.orion[20],
  warning: baseColors.dubloon[30],
  "background-warning": baseColors.dubloon[90],
  "background-warning-secondary": baseColors.dubloon[70],
  info: baseColors.orion[50],
  "background-info": baseColors.orion[90],
  white: baseColors.orion[110],
  border: baseColors.orionAlphaInverse[20],
  "border-strong": baseColors.orionAlphaInverse[30],
  "border-stronger": baseColors.orionAlphaInverse[50],
  "border-subtle": baseColors.orionAlphaInverse[10],
  gold: "#FFD700",
  "upsell-primary": baseColors.ocean[30],
  "upsell-secondary": baseColors.ocean[80],
  "upsell-gem": "#00d4ff",
};

const actualColors = {
  "background_page-filter": baseColors.filter[90],
  "background_page-highlighted":
    "color-mix(in srgb, var(--mb-color-core-brand) 7%, transparent)",
  "background_page-primary": baseColors.orion[100],
  "background_page-primary-inverse": "white",
  "background_page-secondary":
    "color-mix(in srgb, var(--mb-color-background_page-primary), black 25%)",
  "background_page-secondary-inverse": baseColors.orion[5],
  "background_page-tertiary":
    "color-mix(in srgb, var(--mb-color-background_page-primary), black 50%)",
  "background_page-tertiary-inverse": baseColors.orion[10],
  "background_surface-brand-strong": baseColors.brand[20],
  "background_surface-brand-strong-hover": baseColors.brand[10],
  "background_surface-brand-strong-pressed": baseColors.brand[20],
  "background_surface-brand-subtle":
    "color-mix(in srgb, var(--mb-color-core-brand) 10%, transparent)",
  "background_surface-brand-subtle-hover":
    "color-mix(in srgb, var(--mb-color-core-brand), black 50%)",
  "background_surface-brand-subtle-pressed":
    "color-mix(in srgb, var(--mb-color-core-brand), black 75%)",
  "background_surface-disabled": baseColors.orionAlphaInverse[10],
  "background_surface-disabled-inverse": baseColors.orionAlpha[10],
  "background_surface-error": baseColors.lobster[90],
  "background_surface-error-subtle": baseColors.lobster[90],
  "background_surface-hover":
    "color-mix(in srgb, var(--mb-color-core-brand) 7%, transparent)",
  "background_surface-primary": "var(--mb-color-background_page-primary)",
  "background_surface-primary-hover": baseColors.orionAlphaInverse[20],
  "background_surface-primary-inverse":
    "var(--mb-color-background_page-primary-inverse)",
  "background_surface-primary-inverse-hover": baseColors.orionAlpha[10],
  "background_surface-primary-inverse-pressed": baseColors.orionAlpha[20],
  "background_surface-primary-pressed": baseColors.orionAlphaInverse[10],
  "background_surface-secondary": baseColors.orionAlphaInverse[10],
  "background_surface-secondary-hover": baseColors.orionAlphaInverse[20],
  "background_surface-secondary-inverse": baseColors.orionAlpha[10],
  "background_surface-secondary-inverse-hover": baseColors.orionAlpha[20],
  "background_surface-secondary-inverse-pressed": baseColors.orionAlpha[30],
  "background_surface-secondary-pressed": baseColors.orionAlphaInverse[10],
  "background_surface-selected":
    "color-mix(in srgb, var(--mb-color-core-brand) 21%, transparent)",
  "background_surface-success": baseColors.palm[90],
  "background_surface-warning": baseColors.dubloon[90],
  "background_surface-warning-strong": baseColors.dubloon[70],
  "border-brand": baseColors.brand[80],
  "border-filter": baseColors.filter[80],
  "border-neutral": baseColors.orionAlphaInverse[20],
  "border-neutral-strong": baseColors.orionAlphaInverse[30],
  "border-neutral-strongest": baseColors.orionAlphaInverse[50],
  "border-neutral-subtle": baseColors.orionAlphaInverse[10],
  "core-blue-saturated": baseColors.ocean[40],
  "core-brand-hover": baseColors.brand[30],
  "core-gold": "#FFD700",
  "core-green-saturated": sp["green-400"],
  "core-info": baseColors.orion[50],
  "core-metabase_brand": baseColors.brand[40],
  "core-metabase_brand-hover": baseColors.brand[50],
  "core-purple-saturated": baseColors.octopus[40],
  "core-red-saturated": sp["coral-400"],
  "core-white": baseColors.orion[110],
  "core-white_constant": "white",
  "core-yellow-saturated": baseColors.dubloon[30],
  "feedback-negative": sp["coral-400"],
  "feedback-negative-strong": sp["coral-300"],
  "feedback-neutral-strong": baseColors.orionAlphaInverse[30],
  "feedback-positive": sp["green-400"],
  "feedback-positive-selected": sp["green-300"],
  "feedback-positive-strong": sp["green-200"],
  "feedback-warning": baseColors.dubloon[30],
  "feedback-warning-strong": baseColors.dubloon[20],
  "illustration-secondary-brand": baseColors.brand[80],
  "illustration-tertiary-brand": baseColors.brand[90],
  "input-focus": sp["green-400"],
  "modal-overlay": baseColors.orionAlpha[70],
  "navbar-admin": sp["forest-800"],
  "navbar-admin-inverse": baseColors.brand[60],
  "navbar-admin-secondary": baseColors.brand[30],
  "shadow-default": `color-mix(in srgb, ${baseColors.orion[110]} 20%, transparent)`,
  "switch-off": baseColors.orionAlphaInverse[20],
  "text-brand": baseColors.brand[40],
  "text-brand-hover": baseColors.brand[30],
  "text-brand-strong": baseColors.brand[20],
  "text-brand-strong-hover": baseColors.brand[10],
  "text-disabled": baseColors.orionAlphaInverse[40],
  "text-disabled-inverse": baseColors.orionAlpha[40],
  "text-filter": baseColors.filter[30],
  "text-primary": baseColors.orionAlphaInverse[80],
  "text-primary-inverse": baseColors.orionAlpha[80],
  "text-secondary": baseColors.orionAlphaInverse[60],
  "text-secondary-inverse": baseColors.orionAlpha[60],
  "text-selected": baseColors.white,
  "text-syntax-parameter": baseColors.mango[40],
  "text-syntax-parameter-active": baseColors.mango[90],
  "tooltip-background": sp["forest-800"],
  "tooltip-background-focused": sp["forest-900"],
  "tooltip-text": baseColors.orionAlphaInverse[80],
  "tooltip-text-secondary": baseColors.orionAlphaInverse[60],
};

/**
 * The default dark theme for Metabase.
 */
export const METABASE_DARK_THEME: MetabaseThemeV2 = {
  version: 2,
  chartColors: DARK_THEME_ACCENT_COLORS,
  colors: {
    ...deprecatedColors,
    ...actualColors,
  },
};
